import React from "react";
import ReactDOM from 'react-dom';
import PropTypes from "prop-types";
import CardSpecialLabel from './CardComponents/CardSpecialLabel';
import CardMediaBox from './CardComponents/CardMediaBox';
import CardParentBox from './CardComponents/CardParentBox';
import CardTextBox from './CardComponents/CardTextBox';
import CardActionBox from './CardComponents/CardActionBox';
import { formatClassName, pickDataAttributes } from '../Util';
import { onExecuteKeyDown } from '../util/KeyboardUtil';
import Icon from '../components/Icon';
import { kebabCase } from 'lodash';

const BREAKPOINTS = Object.freeze([
  { label: 'extra-wide', width: 1608 },
  { label: 'wide', width: 848 },
  { label: 'medium', width: 692 },
  { label: 'narrow', width: 0 }
]);
/* These BREAKPOINTS are in a specific order and based on the PageWrapper. Touch with care */

/** This component will style a div like a card */
export default class PresentationCard extends React.Component {
  static propTypes = {
    actionsClass: PropTypes.string,
    attentionGrabber: PropTypes.shape({
      className: PropTypes.string,
      icon: PropTypes.string,
      label: PropTypes.string,
      type: PropTypes.string
    }),
    alertMiniCard: PropTypes.bool,
    allowSecondaryDetailLineBreaks: PropTypes.bool,
    buttonConfig: PropTypes.arrayOf(
      PropTypes.shape({
        style: PropTypes.string,
        iconFamily: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string,
        disabled: PropTypes.bool,
        action: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired
      })
    ),
    buttonPosition: PropTypes.oneOf(['top', 'middle', 'bottom']),
    className: PropTypes.string,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    containerClass: PropTypes.string,
    description: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.string
    ]),
    descriptionClass: PropTypes.string,
    descriptionDataAttributes: PropTypes.object,
    dismissable: PropTypes.bool,
    hasRichTextContent: PropTypes.bool,
    isChildCard: PropTypes.bool,
    isTabbable: PropTypes.bool,
    isWrapper: PropTypes.bool,
    mediaContent: PropTypes.node,
    mediaDataAttributes: PropTypes.object,
    mediaUrl: PropTypes.string,
    mediaSize: PropTypes.string,
    mediaPosition: PropTypes.oneOf(['left', 'right']),
    onClick: PropTypes.func,
    onDismiss: PropTypes.func,
    parentChevron: PropTypes.bool,
    parentIcon: PropTypes.string,
    parentLabel: PropTypes.string,
    parentMediaSize: PropTypes.string,
    parentWrapped: PropTypes.bool,
    resizeSelector: PropTypes.string,
    secondaryDetail: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.object)
    ]),
    specialLabel: PropTypes.shape({
      label: PropTypes.string,
      icon: PropTypes.string,
      iconColor: PropTypes.string,
      fontFamily: PropTypes.string,
      fontWeight: PropTypes.string
    }),
    srOnlyText: PropTypes.string,
    stackedButtons: PropTypes.bool,
    styleConfig: PropTypes.shape({
      background: PropTypes.oneOf(['white', 'gray']),
      border: PropTypes.oneOf(['all', 'vertical', 'horizontal']),
      corner: PropTypes.oneOf(['none', 'small', 'xsmall']),
      mediaBackground: PropTypes.oneOf(['white', 'gray', 'light']),
      shadow: PropTypes.oneOf(['none', 'light'])
    }),
    title: PropTypes.string,
    titleDataAttributes: PropTypes.object,
    titleIcon: PropTypes.string,
    titleIconSize: PropTypes.string,
    titleSize: PropTypes.oneOf([14, 16, 18]),
    type: PropTypes.string.isRequired
  };

  static defaultProps = {
    allowSecondaryDetailLineBreaks: false,
    className: '',
    buttonPosition: 'top',
    dismissable: false,
    hasRichTextContent: false,
    isChildCard: false,
    isTabbable: true,
    mediaPosition: 'left',
    mediaSize: 'large',
    parentChevron: true,
    resizeSelector: '.page-wrapper',
    styleConfig: {
      background: 'white',
      border: 'all',
      corner: 'none',
      mediaBackground: 'white',
      shadow: 'none'
    },
    titleSize: 18,
    type: 'wide'
  };

  constructor(props) {
    super(props);
    this.state = {
      cardWidth: 0,
      showChildren: false
    }
  }

  componentDidMount() {
    this.updateCardDimensions();
    window.addEventListener('resize', this.updateCardDimensions);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.cardWidth !== this.state.cardWidth) {
      this.updateCardDimensions();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateCardDimensions)
  }

  updateCardDimensions = () => {
    const container = document.querySelector(this.props.resizeSelector);
    const card = ReactDOM.findDOMNode(this.cardRef);
    if (container) {
      this.setState({ width: container.clientWidth });
    } else {
      this.setState({ width: window.innerWidth });
    }
    if (card) {
      this.setState({ cardWidth: card.getBoundingClientRect().width })
    }
  }

  getButtonCount = () => {
    const { buttonConfig } = this.props;
    if (buttonConfig && buttonConfig.length) {
      return buttonConfig.length
    }
    return;
  }

  getCardType = () => {
    const { type } = this.props;
    const { width } = this.state;
    if (['mini', 'narrow'].includes(type)) {
      return type;
    }
    for (let i = 0, length = BREAKPOINTS.length; i < length; i++) {
      const breakpoint = BREAKPOINTS[i];
      if (width >= breakpoint.width) {
        return breakpoint.label;
      }
    }
    return 'wide';
  }

  toggleChildren = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ showChildren: !this.state.showChildren })
  }

  renderChildCards = () => {
    const { mediaSize, parentLabel } = this.props;
    const parentWrapped = this.getParentClasses().includes('card-parent-wrapped');
    const parentMediaSize = parentLabel ? mediaSize : null;
    const cardType = this.getCardType();

    return (
      <div className="card-child-container">
        {React.Children.map(this.props.children,
          child => React.cloneElement(child, {
            isChildCard: true,
            parentWrapped,
            parentMediaSize,
            type: cardType === 'narrow' ? 'narrow' : cardType
          }))}
      </div>
    );
  }

  onClick = (e) => {
    const { onClick } = this.props;
    if (typeof onClick === 'function') {
      e.preventDefault();
      e.stopPropagation();
      return onClick();
    }
  }

  getCardClasses = () => {
    const { className, styleConfig: { background, border, corner, shadow }, isChildCard, mediaSize, parentWrapped } = this.props;
    const cardType = this.getCardType();
    const clickable = this.hasOnClick();

    return formatClassName([
      `presentation-card`,
      `presentation-card-${cardType}`,
      `card-background-${background}`,
      `card-border-${border}`,
      `card-corner-${corner}`,
      `card-shadow-${shadow}`,
      `card-media-${mediaSize}`,
      { 'child-card': isChildCard },
      { 'parent-wrapped': parentWrapped },
      { 'card-clickable': clickable },
      className
    ])
  }

  hasOnClick = () => Boolean(this.props.onClick);

  hasMedia = () => Boolean(this.props.mediaUrl || this.props.mediaContent);

  hasLabel = () => Boolean(this.props.specialLabel);

  hasChildren = () => Boolean(this.props.children);

  renderSpecialLabel = () => {
    const { specialLabel } = this.props;
    const cardType = this.getCardType();
    if (!specialLabel) {
      return null;
    }
    return (
      <CardSpecialLabel
        {...specialLabel}
        type={cardType}
        hasMedia={this.hasMedia()}
      />
    )
  }

  renderMedia = () => {
    const { mediaContent, mediaDataAttributes, mediaUrl, mediaSize, mediaPosition, styleConfig: { mediaBackground } } = this.props;
    const cardType = this.getCardType();
    if (!mediaUrl && !mediaContent) {
      return null;
    }
    return (
      <CardMediaBox
        mediaBackground={mediaBackground}
        mediaDataAttributes={mediaDataAttributes}
        mediaContent={mediaContent}
        mediaUrl={mediaUrl}
        mediaSize={mediaSize}
        mediaPosition={mediaPosition}
        hasLabel={this.hasLabel()}
        type={cardType} />
    )
  }

  getParentClasses = () => {
    const { mediaSize, mediaPosition } = this.props;
    const { cardWidth } = this.state;
    const cardType = this.getCardType();

    let parentWrapped = false;
    let largeMedia = mediaSize === 'large';
    if (largeMedia) {
      if (mediaPosition === 'right' && cardWidth <= 1081) {
        parentWrapped = true;
      } else if (cardWidth <= 1071) {
        parentWrapped = true;
      }
    } else if (!largeMedia) {
      if (mediaPosition === 'right' && cardWidth <= 951) {
        parentWrapped = true;
      } else if (cardWidth <= 941) {
        parentWrapped = true;
      }
    }
    return formatClassName([
      'card-parent',
      `card-parent-${cardType}`,
      { 'card-parent-wrapped': parentWrapped }
    ])
  }

  renderParent = () => {
    const { parentLabel, parentIcon, parentChevron } = this.props;
    const { showChildren } = this.state;
    if (!parentLabel) {
      return null;
    }
    let className = this.getParentClasses();
    return (
      <CardParentBox
        label={parentLabel}
        icon={parentIcon}
        chevron={parentChevron}
        className={className}
        toggleChildren={this.toggleChildren}
        showChildren={showChildren} />
    )
  }

  renderText = () => {
    const {
      alertMiniCard,
      allowSecondaryDetailLineBreaks,
      attentionGrabber,
      description,
      descriptionClass,
      descriptionDataAttributes,
      hasRichTextContent,
      secondaryDetail,
      styleConfig: { corner },
      title,
      titleDataAttributes,
      titleIcon,
      titleIconSize,
      titleSize
    } = this.props;
    const cardType = this.getCardType();

    if (!title && !description) {
      return null;
    }
    return (
      <CardTextBox
        alertMiniCard={alertMiniCard}
        allowSecondaryDetailLineBreaks={allowSecondaryDetailLineBreaks}
        attentionGrabber={attentionGrabber}
        corner={corner}
        descriptionDataAttributes={descriptionDataAttributes}
        title={title}
        titleSize={titleSize}
        titleIcon={titleIcon}
        titleIconSize={titleIconSize}
        type={cardType}
        description={description}
        descriptionClass={descriptionClass}
        secondaryDetail={secondaryDetail}
        titleDataAttributes={titleDataAttributes}
        hasLabel={this.hasLabel()}
        hasMedia={this.hasMedia()} 
        hasRichTextContent={hasRichTextContent}
      />
    )
  }

  // eslint-disable-next-line complexity
  getActionBoxClasses = () => {
    const {
      actionsClass,
      stackedButtons,
      buttonPosition,
      parentLabel,
      mediaSize,
      isChildCard,
      parentMediaSize,
      parentWrapped
    } = this.props;
    const { cardWidth } = this.state;
    const cardType = this.getCardType();

    let buttonsWrapped = false;
    let buttonCount = this.getButtonCount();
    let largeMedia = mediaSize === 'large';
    let largeParent = parentMediaSize === 'large';
    let hasMedia = this.hasMedia();

    if (buttonCount === 2 && !stackedButtons) {
      if (!isChildCard) {
        if (hasMedia) {
          if (parentLabel) {
            if (largeMedia && cardWidth <= 1300) {
              buttonsWrapped = true;
            } else if (cardWidth <= 1170) {
              buttonsWrapped = true;
            }
          } else if (largeMedia && cardWidth <= 1150) {
            buttonsWrapped = true;
          } else if (!largeMedia && cardWidth <= 1116) {
            buttonsWrapped = true;
          }
        } else if (cardWidth <= 870) {
          buttonsWrapped = true;
        }
      } else {
        if (largeParent && cardWidth <= 1218) {
          buttonsWrapped = true;
        } else if (cardWidth <= 1088) {
          buttonsWrapped = true;
        }
      }
    } else if (buttonCount === 1 || stackedButtons) {
      if (!isChildCard) {
        if (hasMedia) {
          if (parentLabel) {
            if (largeMedia && cardWidth <= 1197) {
              buttonsWrapped = true;
            } else if (cardWidth <= 1067) {
              buttonsWrapped = true;
            }
          } else if (largeMedia && cardWidth <= 1047) {
            buttonsWrapped = true;
          } else if (!largeMedia && cardWidth <= 917) {
            buttonsWrapped = true;
          } else if (!hasMedia && cardWidth <= 767) {
            buttonsWrapped = true;
          }
        } else if (cardWidth <= 767) {
          buttonsWrapped = true;
        }
      } else {
        if (largeParent) {
          if (!parentWrapped && cardWidth <= 1115) {
            buttonsWrapped = true;
          } else if (cardWidth <= 965) {
            buttonsWrapped = true;
          }
        } else {
          if (!parentWrapped && cardWidth <= 985) {
            buttonsWrapped = true;
          } else if (largeMedia && hasMedia && cardWidth <= 859) {
            buttonsWrapped = true;
          } else if (cardWidth <= 835) {
            buttonsWrapped = true;
          }
        }
      }
    }

    return formatClassName([
      'card-actions',
      actionsClass,
      `card-actions-${cardType}`,
      `card-actions-${buttonPosition}`,
      { 'card-actions-wrapped': buttonsWrapped },
      { 'actions-stacked': stackedButtons }
    ])
  }

  renderActions = () => {
    const { buttonConfig, buttonPosition } = this.props;
    const cardType = this.getCardType();
    if (!buttonConfig) {
      return null;
    }
    let className = this.getActionBoxClasses();
    return (
      <CardActionBox
        buttons={buttonConfig}
        className={className}
        buttonPosition={buttonPosition}
        type={cardType} />
    )
  }

  getMediaContainerClasses = () => {
    const { mediaSize } = this.props;
    const noMedia = this.hasMedia();
    return formatClassName([
      'card-media-container',
      `card-media-${mediaSize}`,
      { 'no-media': !noMedia }
    ])
  }

  renderDismissAction = () => {
    const { dismissable, onDismiss } = this.props;

    if (!dismissable || typeof onDismiss !== 'function') return null;

    return <Icon iconType="close" additionalClasses="card-dismiss" onClick={onDismiss} />;
  }

  renderContent = () => {
    const { cardWidth } = this.state;
    const cardType = this.getCardType();
    if (cardType === 'narrow' || cardWidth <= 741) {
      return (
        <React.Fragment>
          {this.renderText()}
          <div className="card-action-content">
            {this.renderParent()}
            {this.renderActions()}
          </div>
        </React.Fragment>
      )
    } else {
      return (
        <React.Fragment>
          {this.renderParent()}
          {this.renderText()}
          {this.renderActions()}
        </React.Fragment>
      )
    }
  }

  render() {
    const { containerClass, isTabbable, isWrapper, mediaPosition, srOnlyText, title } = this.props;
    const { showChildren } = this.state;
    const mediaRight = mediaPosition === 'right';
    const mediaContainerClasses = this.getMediaContainerClasses();
    const cardClasses = this.getCardClasses();
    const tabIndex = isTabbable ? 0 : -1;
    const dataAtrName = title ? `${kebabCase(title)}-presentation-card` : 'presentation-card';
    const dataAttributes = pickDataAttributes(this.props, dataAtrName);

    return (
      <div
        {...dataAttributes}
        className={cardClasses}
        tabIndex={tabIndex}
        onClick={this.onClick}
        onKeyDown={this.hasOnClick() ? onExecuteKeyDown(this.onClick) : undefined}
        ref={(div) => (this.cardRef = div)}
      >
        {srOnlyText && <h2 className="sr-only">{srOnlyText}</h2>}
        {isWrapper &&
          <div className={`card-main-container ${mediaRight ? 'media-right' : ''} ${containerClass}`} >
            <div className="presentation-card-content">
              {this.renderDismissAction()}
              {this.props.children}
            </div>
          </div>}
        {!isWrapper &&
          <React.Fragment>
            <div className={`card-main-container ${mediaRight ? 'media-right' : ''} ${containerClass}`} >
              <div className={mediaContainerClasses}>
                {this.renderSpecialLabel()}
                {this.renderMedia()}
              </div>
              <div className="presentation-card-content">
                {this.renderDismissAction()}
                {this.renderContent()}
              </div>
            </div>
            {showChildren && this.renderChildCards()}
          </React.Fragment>
        }
      </div>
    )
  }
}
